import { CometChat } from "@cometchat/chat-sdk-javascript";
import { parseMeta } from "./status";
import { setUserName } from "./names";

/**
 * The one-time "confirm your name" step. Names used to come from the DBM
 * portal Google Sheet, re-synced on every login — but the sheet can (and
 * does) have duplicate rows for the same email, so which name won on any
 * given login was effectively arbitrary. This replaces that with the one
 * thing that IS reliable (the email itself, from the portal handoff),
 * and asks the person once, directly, what they want to be called.
 *
 * `dbmNameLocked` lives in the user's own CometChat metadata rather than
 * anywhere server-side, so a single client-side check covers both a
 * brand-new user (never set) and an existing user (never set either,
 * since this flag didn't exist before) with no special-casing between
 * the two. The server-side sheet-sync (see _lib/cometchat.js) checks the
 * same flag before ever touching a name again, so once it's set here it
 * genuinely never gets overwritten by an old sheet correction.
 */

export function readNameLocked(user: CometChat.User | null | undefined): boolean {
  return !!parseMeta(user).dbmNameLocked;
}

/** Best-effort split of an existing name into first/last, so the form
 * starts pre-filled with a real guess instead of two blank fields. */
export function splitName(name: string | undefined | null): { first: string; last: string } {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first: "", last: "" };
  if (parts.length === 1) return { first: parts[0], last: "" };
  return { first: parts[0], last: parts.slice(1).join(" ") };
}

/** Sets the name once and locks it — called only from the confirm step,
 * never editable again afterward through this same path. */
export async function setNameOnce(firstName: string, lastName: string): Promise<void> {
  const current = await CometChat.getLoggedinUser();
  if (!current) return;
  const meta = parseMeta(current);
  meta.dbmNameLocked = true;
  meta.dbmFirstName = firstName;
  meta.dbmLastName = lastName;
  const full = `${firstName} ${lastName}`.trim();
  (current as any).setName(full);
  (current as any).setMetadata(meta);
  await CometChat.updateCurrentUserDetails(current);
  // Without this, the new name wouldn't show on this user's OWN past
  // messages until their next full page reload — the exact bug this
  // whole cache exists to fix, just for the person who just renamed.
  const uid = current.getUid?.();
  if (uid) setUserName(uid, full);
}
